// CraftReadTab.js — the Craft Read tab in the Layered Read scene panel.
//
// Craft Read = the product name for the traditional criteria (the legacy
// per-scene analysis columns: concept, plot, characters, dialogue, …). This
// tab shows that reading in the SAME rail/spotlight idiom NodePanel uses for
// Scene Mechanics, so the two instruments feel like two vocabularies for one
// page, not two products. IA (redesign 2026-08-12, decision record in
// CONNECTIONS_TAB_D0.md): Diagnosis (Scene Mechanics) | Craft Read | Read the
// scene — no Connections tab; convergence renders inside BOTH instrument
// views as reciprocal ConvergenceModules that jump to each other.
//
// Data: GET /sos2/craftread — the scene row's legacy criteria (score,
// importance, explanation), server-filtered to criteria whose explanation is
// non-empty (same validity rule the bridge uses for traditional endpoints).
// Admin-gated like the Connections endpoint; {enabled:false} → the tab does
// not exist. No copy transform is needed: this is the writer's own analysis
// prose, already writer-facing — the naming rules only bind the CONVERGENCE
// copy, which arrives through the gate like everywhere else.

import React, { useState, useEffect } from 'react';
import { ConvergenceModule, criterionLabel } from './ConnectionsTab';
import { tip } from './Tip';
import { fmtScore } from './Constants';

// ONE ENDPOINT. `/sos2/craftread` authorizes per movie with the standard analysis rule
// (admin all · anyone public · owners their own).
//
// ⚠️ PREFER THE HASH. `?movie_id` is ADMIN-ONLY — the numeric id is enumerable, so it stays an
// internal form exactly as `AnalysisController::icicle()` treats `?id`. A non-admin OWNER sending
// movie_id is refused, so preferring movieId here (as this hook first did) would have made the tab
// vanish for every ordinary owner while working perfectly for the admin testing it.
export function useCraftRead(movieId, sceneNumber, hash = null) {
  const [data, setData] = useState(null);   // { criteria, groups, overview } | null
  useEffect(() => {
    let live = true;
    setData(null);
    if ((!movieId && !hash) || !sceneNumber) return () => { live = false; };
    const url = hash
      ? `/sos2/craftread?hash=${encodeURIComponent(hash)}&scene_number=${sceneNumber}`
      : `/sos2/craftread?movie_id=${movieId}&scene_number=${sceneNumber}`;
    fetch(url, { headers: { Accept: 'application/json' } })
      .then(r => (r.ok ? r.json() : { enabled: false, criteria: [] }))
      .then(d => {
        if (live && d.enabled && ((d.criteria || []).length || d.overview)) {
          // Field-by-field on purpose (not a spread) so nothing unexpected from the server lands in
          // component state. That means EVERY new response field must be added here — `groups` was
          // added 2026-08-21 and was silently dropped until it was, which renders as an ungrouped
          // list with no error anywhere.
          setData({ criteria: d.criteria || [], groups: d.groups || null, overview: d.overview || null });
        }
      })
      .catch(() => {});
    return () => { live = false; };
  }, [movieId, sceneNumber, hash]);
  return data;
}

const NAVY = '#1f3a5f';
const CRAFT = '#6d4c8f';

function scoreText(s) {
  if (s == null || s === '' || !Number.isFinite(parseFloat(s))) return null;
  const n = parseFloat(s);
  return fmtScore(n);   // was a local copy; one definition now lives in Constants.js
}

// The scene grid's score ramp, verbatim from `partials/analysis/sequence_styles.blade.php`
// (.grade0 … .grade10). Same numbers must read as the same colour on both instruments, so this is
// a copy of a stylesheet the React bundle cannot import — if that ramp is ever retuned, retune it
// here too. Index = rounded score, clamped 0–10.
const GRADE = ['#B23A30', '#CE4F40', '#E07256', '#ED9A6B', '#F3C08C', '#EFE9D2',
               '#DEE7C4', '#ADD68F', '#7DC368', '#4FA84E', '#2E8B43'];
function gradeColor(score) {
  if (score == null || !Number.isFinite(parseFloat(score))) return null;
  return GRADE[Math.max(0, Math.min(10, Math.round(parseFloat(score))))];
}
// The grid puts dark text on every chip, which is unreadable on the three darkest reds. Same
// backgrounds, legible foreground — the colour language is what has to match, not the contrast bug.
function gradeInk(score) {
  const n = parseFloat(score);
  return (Number.isFinite(n) && (n <= 2.5 || n >= 8.5)) ? '#fff' : '#15324d';
}

function CriterionRow({ crit, active, onPick, convCount, pointer }) {
  const s = scoreText(crit.score);
  const fillW = s == null ? 0 : Math.max(2, (parseFloat(s) / 10) * 100);
  const gc = gradeColor(crit.score);
  return (
    <button type="button" onClick={() => onPick(crit.key)}
      className="w-100 text-start d-flex align-items-center gap-2 px-2 py-1"
      style={{ position: 'relative', border: 'none', borderLeft: `3px solid ${active ? CRAFT : 'transparent'}`,
        background: active ? '#f3eef9' : 'transparent', borderRadius: active ? '0 7px 7px 0' : 7, cursor: 'pointer' }}>
      <span style={{ width: 148, flexShrink: 0, color: '#0f172a', fontWeight: active ? 700 : 500, fontSize: '.9rem' }}>
        {crit.label || criterionLabel(crit.key)}
      </span>
      <span style={{ flex: 1, height: 7, background: '#f0edf5', borderRadius: 4, position: 'relative', minWidth: 50 }}>
        <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${fillW}%`,
          background: gc || '#e2e8f0', borderRadius: 4 }} />
      </span>
      {/* Score chip carries the grid's grade colour so a 4 here and a 4 in the scene table read as
          the same thing. `light` = low importance — the Craft Read analogue of "not owed here", so
          the chip is muted rather than alarming. */}
      <span style={{ minWidth: 30, padding: '1px 7px', textAlign: 'center', fontWeight: 700, fontSize: '.88rem',
        borderRadius: 6, background: gc || 'transparent', color: gc ? gradeInk(crit.score) : '#aab',
        opacity: crit.light ? 0.45 : 1 }}
        {...tip(crit.band ? (crit.light ? `${crit.band} · low importance here` : crit.band) : null)}>
        {s == null ? '—' : s}
      </span>
      {/* ◇ = another approved reading of a moment on this page — NOT good/bad/agreement/confidence */}
      {convCount > 0 && (
        <span {...tip('Also identified in Scene Mechanics')} style={{ width: 20, textAlign: 'center', flexShrink: 0, color: '#5b7ba6', fontWeight: 700, fontSize: '.74rem' }}>
          ◇{convCount > 1 ? convCount : ''}
        </span>
      )}
      {active && pointer && <span aria-hidden style={{ position: 'absolute', right: -10, top: '50%', transform: 'translateY(-50%)', width: 0, height: 0, borderTop: '7px solid transparent', borderBottom: '7px solid transparent', borderLeft: `9px solid ${CRAFT}` }} />}
      <span style={{ width: 14, textAlign: 'center', color: active ? CRAFT : '#cbd5e1', fontWeight: 800 }}>{active ? '▸' : '›'}</span>
    </button>
  );
}

// ── Per-criterion coaching extras ────────────────────────────────────────────────────────────
// The rewrite moves and the fix/learn/explore questions live in deepseek_result['coaching'],
// a nested sub-key with no flat column and therefore no model overlay — see the note in
// ConnectionsController::craftread(). Until 2026-08-21 this panel showed only score +
// explanation, i.e. the two fields the legacy scene-table modal renders ABOVE the part writers
// actually use. Copy and colours are deliberately kept identical to that modal
// (`partials/analysis/criterion_coaching_modal.blade.php:134-144`) so the same criterion does
// not get two different recommendations depending on which surface you opened.
const REC = {
  fix:      { chip: 'Revise', color: '#c0563f', text: 'Worth a rewrite pass here.' },
  protect:  { chip: 'Keep',   color: '#4a8a5a', text: 'This is working — protect it through revision.' },
  optional: { chip: 'Polish', color: '#d59232', text: 'Solid here — sharpen only if you want to push it.' },
  leave:    { chip: 'Fine',   color: '#7c93a5', text: 'Nothing needed here.' },
};
const Q_TYPE = { fix: '#c0563f', learn: '#2b6ea8', explore: '#6d4c8f' };
const Q_LEGEND = 'Fix = a practical change · Learn = the principle behind it · Explore = a what-if';

function Chip({ text, color }) {
  return (
    <span style={{ display: 'inline-block', fontSize: '.66rem', fontWeight: 800, textTransform: 'uppercase',
      letterSpacing: '.05em', color: '#fff', background: color, borderRadius: 4, padding: '2px 8px',
      marginRight: 8, verticalAlign: 'middle' }}>{text}</span>
  );
}

// NO EYEBROW LABELS (house rule): a section label must never be smaller or quieter than the
// content it labels. Body here is .9rem, so these sit above it rather than below. This is a
// DELIBERATE divergence from the legacy modal, which renders "ASSESSMENT" / "QUESTIONS TO
// EXPLORE" as small uppercase eyebrows — the wording is kept identical to that modal, the
// typography is not.
function SubHead({ children }) {
  return (
    <div style={{ fontWeight: 700, color: CRAFT, fontSize: '.96rem',
      margin: '16px 0 7px', paddingBottom: 5, borderBottom: '1px solid #ece7f3' }}>
      {children}
    </div>
  );
}

// ASKING A QUESTION (owner, 2026-08-21). The questions are now live: clicking one seeds the panel's
// OWN docked chat (`AxisChat`) through the same `onDiscussAxis` path a DER axis uses, so a criterion
// question and an axis question share one chat and one thread model.
//
// ⚠️ This is deliberately NOT the legacy `interactWithAssistant`. That is a widget controller, not an
// API — it is defined inside `partials/assistant_partial.blade.php` and drives a form keyed
// `message_<element><sceneNumber>`, so it only works where that partial has been included (the Blade
// scene modal includes one per scene). Wiring it here would mean two chat surfaces in one panel.
//
// `onAsk` is null when the host provides no chat, and then no ask affordance renders at all —
// never a dead control.
function CoachingBlock({ coaching, critKey, critLabel, onAsk }) {
  if (!coaching) return null;
  // ⚠️ `priority` also comes in two shapes. The grouped assembler sends an OBJECT carrying its own
  // chip/text/colours ({chip,cls,text,fg,bg,key}); the flat endpoint sends a bare key string. Using
  // the object's own fields keeps this chip identical to the Blade rail's without a second copy of
  // the vocabulary — REC below is only the fallback for the string shape. (Before this, an object
  // stringified to "[object Object]", matched nothing, and the chip just never appeared.)
  // The priority chip + its sentence are rendered by CriterionSpotlight's HEADER now, mirroring the
  // Blade. REC survives only as the fallback vocabulary for the older flat shape, where `priority`
  // arrives as a bare key string with no chip/colour of its own.
  const p = coaching.priority;
  const pk = (p && typeof p === 'object') ? '' : String(p || '').toLowerCase();
  const recFallback = pk ? (REC[pk] || null) : null;
  const moves    = asItems(coaching.moves);
  const questions = Array.isArray(coaching.questions) ? coaching.questions : [];

  if (!recFallback && !moves.length && !questions.length && !coaching.example && !coaching.tradeoff && !coaching.craft_note) {
    return null;
  }

  return (
    <>
      {recFallback && (
        <div style={{ margin: '10px 0 2px', fontSize: '.9rem', color: '#2b3a45', lineHeight: 1.5 }}>
          <Chip text={recFallback.chip} color={recFallback.color} />{recFallback.text}
        </div>
      )}

      {/* PROBLEM FIRST, THEN THE MOVE — and the first move leads as its own line rather than as
          bullet one, because it is the one thing to do. Same treatment as the Blade. */}
      {moves.length > 0 && (
        <>
          <SubHead>Do this first</SubHead>
          <div style={{ margin: 0, fontSize: '.94rem', fontWeight: 600, color: '#0f172a', lineHeight: 1.5 }}>{moves[0]}</div>
          {moves.length > 1 && (
            <ul style={{ margin: '7px 0 0', paddingLeft: 20, color: '#334155', fontSize: '.9rem', lineHeight: 1.55 }}>
              {moves.slice(1).map((m, i) => <li key={i} style={{ marginBottom: 4 }}>{m}</li>)}
            </ul>
          )}
        </>
      )}

      {/* The rewritten example gets its own heading, not a bare quote: it is the one thing here that
          is PROSE FROM THE SCENE rather than commentary about it. */}
      {coaching.example && (
        <>
          <SubHead>A stronger version</SubHead>
          <div style={{ borderLeft: '3px solid #0e7c86', background: '#eef6f5', padding: '9px 13px',
            borderRadius: '0 6px 6px 0', fontSize: '.9rem', color: '#24424a', lineHeight: 1.55 }}>
            {coaching.example}
          </div>
        </>
      )}

      {/* Secondary rows: a labelled two-column grid, not inline prose — these are asides about the
          move, and should read as a different register from the move itself. */}
      {(coaching.tradeoff || coaching.craft_note) && (
        <div style={{ marginTop: 14, borderTop: '1px solid #e7edf1', paddingTop: 10, display: 'grid', gap: 8 }}>
          {[['tradeoff', coaching.tradeoff], ['craft note', coaching.craft_note]]
            .filter(([, v]) => v).map(([k, v]) => (
              <div key={k} style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 10, fontSize: '.88rem' }}>
                <span style={{ fontSize: '.75rem', fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '.05em', color: '#64748b' }}>{k}</span>
                <span style={{ color: '#334155', lineHeight: 1.55 }}>{v}</span>
              </div>
            ))}
        </div>
      )}

      {questions.length > 0 && (
        <>
          <SubHead>Questions to explore</SubHead>
          {/* #94a3b8 at .76rem failed the contrast check against white — this legend is the key to
              three chips a reader has never seen before, so it cannot be the faintest thing here. */}
          <div style={{ fontSize: '.8rem', color: '#64748b', marginBottom: 9 }}>{Q_LEGEND}</div>
          {questions.map((q, i) => {
            const text = (q && typeof q === 'object') ? (q.q || '') : String(q || '');
            const type = (q && typeof q === 'object') ? String(q.type || '').toLowerCase() : '';
            if (!text) return null;
            return (
              <div key={i} style={{ margin: '0 0 8px', fontSize: '.9rem', color: '#2b3a45', lineHeight: 1.55 }}>
                {type && <Chip text={type} color={Q_TYPE[type] || CRAFT} />}
                {onAsk ? (
                  <button type="button" onClick={() => onAsk(critKey, critLabel, text)}
                    {...tip('Ask the AI this question')}
                    style={{ background: 'none', border: 'none', padding: 0, margin: 0, textAlign: 'left',
                      font: 'inherit', color: '#2b3a45', cursor: 'pointer', borderBottom: `1px dashed ${CRAFT}66` }}>
                    {text}
                  </button>
                ) : text}
              </div>
            );
          })}
          {onAsk && (
            <button type="button" onClick={() => onAsk(critKey, critLabel, false)}
              style={{ background: 'none', border: 'none', padding: 0, marginTop: 2, font: 'inherit',
                fontSize: '.86rem', fontWeight: 700, color: CRAFT, cursor: 'pointer' }}>
              Ask your own →
            </button>
          )}
        </>
      )}
    </>
  );
}

// ⚠️ THIS IS A PORT OF `partials/analysis/scene_craft_read.blade.php`, NOT AN INDEPENDENT DESIGN
// (owner, 2026-08-24: "they can't share a component but they can copy the other instead of
// inventing"). Both surfaces render the SAME `craft_read_scene()` payload, and for a while they
// each had their own invented chrome — different section labels, a different order, and React
// silently dropping `pattern_flag` altogether. Two readings of one criterion must not look like two
// products. PHP and React cannot share a component, so the Blade is the reference implementation
// and this mirrors it: same order, same labels, same colours. **If you change one, change both.**
//
// Order is deliberate and documented there: header (label · score/band OR "barely applies here" ·
// priority chip) → priority text → light note → pattern flag → Assessment → Do this first →
// A stronger version → tradeoff/craft note → Questions to explore. The pattern flag sits ABOVE the
// assessment because it changes how the assessment should be read.
function CriterionSpotlight({ crit, groups, onEvent, onShowOnPage, hiddenConn, onHide, onOpenAxis, axisLabel, onAsk }) {
  const s = scoreText(crit.score);
  const prio = (crit.priority && typeof crit.priority === 'object') ? crit.priority : null;
  const flag = (crit.pattern_flag && typeof crit.pattern_flag === 'object') ? crit.pattern_flag : null;
  return (
    <div className="mt-1 mb-2 ms-3 p-3" style={{ border: `1px solid ${CRAFT}33`, borderLeft: `4px solid ${CRAFT}`, borderRadius: 12, background: '#fbfafd', scrollMarginTop: 64 }}>
      <div className="d-flex align-items-center flex-wrap" style={{ gap: 8, marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #ece7f3' }}>
        <span style={{ fontWeight: 800, color: CRAFT, fontSize: '1.02rem' }}>{crit.label || criterionLabel(crit.key)}</span>
        {/* A NOT-OWED DIMENSION MUST NOT LEAD WITH A BAND WORD. Amelie scene 1 is a character-free
            prologue: Dialogue scores 0 while the model's own read calls the narration a deliberate
            choice. Printing "0/10 fail" beside that calls a deliberate choice a failure. */}
        {crit.light ? (
          <>
            <span style={{ fontSize: '.78rem', fontWeight: 700, color: '#64748b', background: '#eef2f5',
              border: '1px solid #d3dee4', borderRadius: 6, padding: '1px 8px' }}>barely applies here</span>
            {s != null && <span style={{ fontSize: '.92rem', fontWeight: 600, color: '#64748b', opacity: .75 }}>{s}/10</span>}
          </>
        ) : s != null && (
          <>
            <span style={{ fontSize: '.95rem', fontWeight: 700, color: '#0f172a' }}>{s}/10</span>
            {crit.band && <span style={{ fontSize: '.78rem', color: '#64748b' }}>{crit.band}</span>}
          </>
        )}
        {prio && (
          <span style={{ display: 'inline-block', fontSize: '.66rem', fontWeight: 800, textTransform: 'uppercase',
            letterSpacing: '.05em', borderRadius: 4, padding: '2px 8px',
            background: prio.bg || CRAFT, color: prio.fg || '#fff' }}>{prio.chip}</span>
        )}
      </div>

      {prio?.text && <div style={{ margin: '0 0 10px', fontSize: '.88rem', color: '#2b3a45' }}>{prio.text}</div>}
      {crit.light && (
        <div style={{ margin: '0 0 10px', fontSize: '.84rem', color: '#64748b' }}>
          This scene isn’t built to carry this, so a low reading here is the shape of the scene rather than a fault.
        </div>
      )}

      {/* Local or systemic? Rare by design (~0.3–0.6 per scene, measured), and silent otherwise —
          which is why it is allowed to be visible. React dropped this entirely until 2026-08-24. */}
      {flag?.note && (
        <div style={{ margin: '0 0 10px', fontSize: '.88rem', lineHeight: 1.45, padding: '7px 11px',
          borderRadius: 6, borderLeft: '3px solid #7fa8bf', color: '#2b4a5c', background: '#eef5f9', maxWidth: '70ch' }}>
          {flag.note}
        </div>
      )}

      {crit.explanation && (
        <>
          <SubHead>Assessment</SubHead>
          <div style={{ margin: 0, color: '#0f172a', fontSize: '.94rem', lineHeight: 1.55 }}>{crit.explanation}</div>
        </>
      )}

      {/* ⚠️ TWO RESPONSE SHAPES. The grouped assembler (`craft_read_scene()`) returns priority /
          moves / questions / example / tradeoff / craft_note at the row's TOP LEVEL; the older flat
          endpoint nested them under `coaching`. When the endpoint switched to the assembler this
          silently rendered NOTHING for a while — `crit.coaching` was simply undefined and the block
          bailed out with no error. Accept both, grouped first. */}
      <CoachingBlock coaching={crit.coaching || crit} critKey={crit.key}
        critLabel={crit.label || criterionLabel(crit.key)} onAsk={onAsk} />
      {/* CONVERGENCE — after the criterion's own prose, never before (corroboration,
          not badge). The mirror of the module in the axis spotlight: names the
          Scene Mechanics axis and jumps there. Human-approved pairings only. */}
      {(groups || []).map((g, gi) => (
        <ConvergenceModule key={`${g.pairing.approval.approved_key}:${gi}`} group={g}
          side="craft" onEvent={onEvent} onShowOnPage={onShowOnPage}
          hidden={hiddenConn && hiddenConn.has(g.pairing.approval.approved_key)} onHide={onHide}
          onOpenOther={onOpenAxis} otherLabel={axisLabel} />
      ))}
    </div>
  );
}

function asItems(v) {
  if (v == null) return [];
  return Array.isArray(v) ? v.filter(x => typeof x === 'string' && x.trim() !== '') : [String(v)];
}

// A titled list card for the overview zone. tone shifts the accent only —
// the content is the legacy instrument's own prose, shown as written.
function OverviewList({ title, items, tone }) {
  if (!items.length) return null;
  const accent = { plus: '#3d7a55', minus: '#b06a3b', neutral: CRAFT }[tone || 'neutral'];
  return (
    <div style={{ flex: '1 1 260px', minWidth: 240 }}>
      <div style={{ fontWeight: 700, color: accent, fontSize: '.86rem', marginBottom: 4 }}>{title}</div>
      <ul style={{ margin: 0, paddingLeft: '1.1rem', color: '#334155', fontSize: '.88rem', lineHeight: 1.5 }}>
        {items.map((s, i) => <li key={i} style={{ marginBottom: 3 }}>{s}</li>)}
      </ul>
    </div>
  );
}

function ExpertBlocks({ experts }) {
  if (!experts || typeof experts !== 'object' || Array.isArray(experts)) return null;
  const entries = Object.entries(experts).filter(([, v]) => typeof v === 'string' && v.trim() !== '');
  if (!entries.length) return null;
  return entries.map(([who, text]) => (
    <div key={who} style={{ marginTop: 8 }}>
      <div style={{ fontWeight: 700, color: '#475569', fontSize: '.82rem' }}>{who}</div>
      <div style={{ color: '#334155', fontSize: '.88rem', lineHeight: 1.55 }}>{text}</div>
    </div>
  ));
}

// The full legacy scene analysis, above the criteria (owner 2026-08-12:
// "everything from LR without leaving the tool"). Strengths/weaknesses up
// front; the long critique and suggestions passes collapsed by default so the
// criteria rail stays one scroll away; expert variants inside each fold.
function CraftOverview({ overview }) {
  const [openCrit, setOpenCrit] = useState(false);
  const [openSugg, setOpenSugg] = useState(false);
  if (!overview) return null;
  const strengths = asItems(overview.strengths);
  const weaknesses = asItems(overview.weaknesses);
  const critique = asItems(overview.critique);
  const suggestions = asItems(overview.suggestions);
  // memorable_line is now {speaker, line} from the server. Deliberately tolerant of the OLD shapes
  // too — a flattened [speaker, line] array or a bare string — because `laravel/public/js` is
  // gitignored and each environment builds its own bundle, so PHP and JS do NOT deploy together.
  // Reading the old shape with new JS (or vice versa) must degrade, never print "[object Object]".
  const memorable = (() => {
    const m = overview.memorable_line;
    if (!m) return null;
    if (typeof m === 'string') return { line: m, speaker: null };
    if (Array.isArray(m)) {
      const v = m.map(x => String(x || '').trim()).filter(Boolean);
      if (!v.length) return null;
      // Two entries is [speaker, line]; one is the line on its own.
      return v.length >= 2 ? { speaker: v[0], line: v[1] } : { speaker: null, line: v[0] };
    }
    const line = String(m.line || '').trim();
    return line ? { line, speaker: String(m.speaker || '').trim() || null } : null;
  })();
  const fold = (label, open, setOpen, items, experts) => {
    if (!items.length && !experts) return null;
    return (
      <div style={{ marginTop: 10 }}>
        <button type="button" onClick={() => setOpen(o => !o)}
          style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
            fontWeight: 700, color: CRAFT, fontSize: '.9rem' }}>
          {open ? '▾' : '▸'} {label}
        </button>
        {open && (
          <div style={{ marginTop: 4 }}>
            {items.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: '1.1rem', color: '#334155', fontSize: '.88rem', lineHeight: 1.55 }}>
                {items.map((s, i) => <li key={i} style={{ marginBottom: 4 }}>{s}</li>)}
              </ul>
            )}
            <ExpertBlocks experts={experts} />
          </div>
        )}
      </div>
    );
  };
  // Order (owner 2026-08-12): what a writer wants first — the judgment
  // (strengths/weaknesses), then the critique and suggestions passes, and the
  // memorable line last, as a small footer, not an epigraph.
  return (
    <div style={{ border: '1px solid #ece7f3', borderRadius: 12, background: '#fdfcfe', padding: '.85rem 1rem', marginTop: '.6rem', marginBottom: '.9rem' }}>
      {(strengths.length > 0 || weaknesses.length > 0) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.2rem' }}>
          <OverviewList title="Strengths" items={strengths} tone="plus" />
          <OverviewList title="Weaknesses" items={weaknesses} tone="minus" />
        </div>
      )}
      {fold('Critique', openCrit, setOpenCrit, critique, overview.experts_critique)}
      {fold('Suggestions', openSugg, setOpenSugg, suggestions, overview.experts_suggestions)}
      {memorable && (
        <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px dashed #ece7f3' }}>
          <span style={{ color: '#94a3b8', fontSize: '.68rem', fontWeight: 800, letterSpacing: '.07em', textTransform: 'uppercase', marginRight: 8 }}>Memorable line</span>
          <span style={{ color: '#4a5a68', fontSize: '.88rem', fontStyle: 'italic' }}>“{memorable.line}”</span>
          {memorable.speaker && (
            <span style={{ color: '#94a3b8', fontSize: '.82rem', marginLeft: 6 }}>— {memorable.speaker}</span>
          )}
        </div>
      )}
    </div>
  );
}

// The tab body. Two-column when the panel is (rail left, spotlight right);
// stacked in the single-column panel (spotlight opens under its row).
export default function CraftReadTab({ criteria, overview = null, twocol = false, focus, onFocus,
  convByCriterion, onEvent, onShowOnPage, hiddenConn, onHide, onOpenAxis, axisLabel, groups = null, onAsk = null }) {
  if ((!criteria || !criteria.length) && !overview) return null;
  criteria = criteria || [];
  const convCount = (key) => ((convByCriterion && convByCriterion[key]) || [])
    .filter(g => !hiddenConn || !hiddenConn.has(g.pairing.approval.approved_key)).length;
  const spotlight = (key) => {
    const crit = criteria.find(c => c.key === key);
    if (!crit) return null;
    return (
      <CriterionSpotlight crit={crit} groups={(convByCriterion && convByCriterion[key]) || []}
        onEvent={onEvent} onShowOnPage={onShowOnPage} hiddenConn={hiddenConn} onHide={onHide}
        onOpenAxis={onOpenAxis} axisLabel={axisLabel} onAsk={onAsk} />
    );
  };
  const critRow = (c, onPick) => (
    <CriterionRow key={c.key} crit={c} active={focus === c.key} onPick={onPick || onFocus}
      convCount={convCount(c.key)} pointer={twocol} />
  );

  // GROUPED RAIL. `groups` is the server's ORDERED list, from craft_read_groups() in
  // craft_read_view.php — the same five display groups, in the same order, the scene grid uses.
  // Grouping is not decoration: eighteen ungrouped criteria read as a wall, and the grid has
  // already taught the reader these five headings. Falls back to a flat list when the server sent
  // no groups (the legacy path on environments without the root PHP files).
  const byKey = new Map(criteria.map(c => [c.key, c]));
  const useGroups = Array.isArray(groups) && groups.length > 0;
  const rows = useGroups
    ? groups.map(g => {
        const items = (g.keys || []).map(k => byKey.get(k)).filter(Boolean);
        if (!items.length) return null;
        return (
          <div key={g.name} style={{ marginBottom: 14 }}>
            <div style={{ color: CRAFT, fontWeight: 700, fontSize: '.86rem', letterSpacing: '.02em',
              margin: '0 0 4px 2px' }}>{g.name}</div>
            {items.map(c => critRow(c))}
          </div>
        );
      })
    : criteria.map(c => critRow(c));
  if (twocol) {
    return (
      <div>
        <CraftOverview overview={overview} />
        <div className="d-flex" style={{ gap: '1.5rem', marginTop: '.6rem', alignItems: 'flex-start' }}>
          <div style={{ flex: '0 0 clamp(230px, 32%, 320px)', minWidth: 0 }}>{rows}</div>
          <div style={{ flex: 1, minWidth: 0, paddingLeft: '1.5rem' }}>
            {focus ? spotlight(focus)
              : <div style={{ color: '#64748b', fontSize: '.92rem', lineHeight: 1.5, padding: '1rem 0' }}>Pick a criterion on the left to see its read.</div>}
          </div>
        </div>
      </div>
    );
  }
  // Single column: the spotlight opens UNDER its own row, so the rows cannot be pre-rendered as
  // above — each one is followed by its own conditional spotlight. Same grouping, same order.
  const stackedRow = (c) => (
    <div key={c.key}>
      <CriterionRow crit={c} active={focus === c.key} onPick={(k) => onFocus(focus === k ? null : k)}
        convCount={convCount(c.key)} pointer={false} />
      {focus === c.key && spotlight(c.key)}
    </div>
  );
  return (
    <div style={{ marginTop: '.6rem' }}>
      <CraftOverview overview={overview} />
      {useGroups
        ? groups.map(g => {
            const items = (g.keys || []).map(k => byKey.get(k)).filter(Boolean);
            if (!items.length) return null;
            return (
              <div key={g.name} style={{ marginBottom: 14 }}>
                <div style={{ color: CRAFT, fontWeight: 700, fontSize: '.86rem', letterSpacing: '.02em',
                  margin: '0 0 4px 2px' }}>{g.name}</div>
                {items.map(stackedRow)}
              </div>
            );
          })
        : criteria.map(stackedRow)}
    </div>
  );
}
